import { Constants } from '@pwc/pwc-deals-web-common';
import updateEntityVersion from '../../actions/updateEntityVersion';
import {
  getAllDocumentsWithContentForProjectAndViewingType,
  getStructuredAnalysisVersions,
} from '../services/structuredAnalysisService';
import { dispatchLibraryItemLinksSet } from './dispatchLibraryItemLinksSet';
import { transformIngestedDataByDocumentCode } from './transformIngestedDataByDocumentCode';
import { dispatchLinkedAdjustmentsForDocument } from './dispatchLinkedAdjustmentsForDocument';
import { descriptionResetToBasisOfPresentation } from '../reducers/structuredDocumentDescriptionSlice';
import {
  processingFailed,
  processingSucceeded,
  startProcessing,
} from '../reducers/structuredAnalysisViewSlice';

const fetchVersionsForDocuments = async (projectId, documents) => {
  const results = await Promise.all(
    documents.map((doc) => {
      return getStructuredAnalysisVersions(
        projectId,
        doc.content.contentThreadId
      );
    })
  );
  return results;
};

export const addVersionsToDocumentsWithContent = (
  documentsWithContent,
  versionsForDocuments
) => {
  const documentsWithContentAndVersions = documentsWithContent.map((x) => {
    const documentVersions = versionsForDocuments.find(
      (y) => y.analysisId === x.content.contentThreadId
    );
    return {
      ...x,
      versions: documentVersions ? documentVersions.data : [],
    };
  });

  return documentsWithContentAndVersions;
};

export const removeRawCellDataFromDocuments = (documents) => {
  return documents.map((x) => {
    const cloone = { ...x };
    delete cloone.rawCellData; // For redux-dev-tools
    return cloone;
  });
};

const processSingleDocument = async (
  projectId,
  document,
  dispatch,
  getState,
  isViewingAsClient
) => {
  await dispatchLibraryItemLinksSet(
    true,
    projectId,
    document.code,
    document.content.contentThreadId,
    document.content.versionNumber,
    dispatch,
    isViewingAsClient
  );
  await dispatchLinkedAdjustmentsForDocument(
    projectId,
    document.code,
    dispatch
  );
  // calc-engine
  await transformIngestedDataByDocumentCode(
    document.code,
    JSON.parse(document.rawCellData),
    document.configurationValues,
    dispatch
  );
};

export const transformAllIngestedDataThunk =
  (
    projectId,
    documentCodeForReview,
    isViewingAsClient = true,
    isEditMode = false
  ) =>
  async (dispatch, getState) => {
    dispatch(startProcessing());

    let documentsWithContent = [];
    try {
      documentsWithContent =
        await getAllDocumentsWithContentForProjectAndViewingType(
          projectId,
          isViewingAsClient
        );
    } catch (error) {
      dispatch(processingFailed());
      return;
    }

    const contentAnalysis = documentsWithContent.find(
      (x) => x.code === documentCodeForReview
    );

    if (!contentAnalysis) {
      dispatch(processingFailed());
      return;
    }

    const versionsForDocuments = await fetchVersionsForDocuments(
      projectId,
      documentsWithContent
    );

    const documentsWithContentAndVersions = addVersionsToDocumentsWithContent(
      documentsWithContent,
      versionsForDocuments
    );

    const { content = {} } = contentAnalysis;
    const { versionStatus = 9 } = content;
    const isViewAsClient = isEditMode
      ? false
      : versionStatus === Constants.VERSION_STATUS.SHARED.value ||
        versionStatus ===
          Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value;
    await Promise.all(
      documentsWithContentAndVersions.map(async (document) =>
        processSingleDocument(
          projectId,
          document,
          dispatch,
          getState,
          isViewAsClient
        )
      )
    );

    const trimmedDocumentsWithContent = removeRawCellDataFromDocuments(
      documentsWithContentAndVersions
    );

    // // TODO: these things are prime refactors
    // dispatch(basisOfPresentationSet(basisOfPresentation));
    dispatch(descriptionResetToBasisOfPresentation());
    dispatch(updateEntityVersion(contentAnalysis.content.versionNumber));

    dispatch(
      processingSucceeded({
        documentsWithContent: trimmedDocumentsWithContent,
      })
    );
  };

export const transformAllIngestedDocumentsThunk =
  (projectId, documentCodesForReview, isViewingAsClient = true) =>
  async (dispatch, getState) => {
    dispatch(startProcessing());

    let documentsWithContent = [];
    try {
      documentsWithContent =
        await getAllDocumentsWithContentForProjectAndViewingType(
          projectId,
          isViewingAsClient
        );
    } catch (error) {
      dispatch(processingFailed());
      return;
    }

    const versionsForDocuments = await fetchVersionsForDocuments(
      projectId,
      documentsWithContent
    );

    const documentsWithContentAndVersions = addVersionsToDocumentsWithContent(
      documentsWithContent,
      versionsForDocuments
    );

    await Promise.all(
      documentsWithContentAndVersions.map(async (document) =>
        processSingleDocument(
          projectId,
          document,
          dispatch,
          getState,
          document.content.versionStatus ===
            Constants.VERSION_STATUS.SHARED.value ||
            document.content.versionStatus ===
              Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
        )
      )
    );

    documentCodesForReview.forEach(async (documentCodeForReview) => {
      const contentAnalysis = documentsWithContent.find(
        (x) => x.code === documentCodeForReview
      );

      // if (!contentAnalysis) {
      //   console.log(contentAnalysis);
      //   dispatch(processingFailed());
      //   return;
      // }

      const trimmedDocumentsWithContent = removeRawCellDataFromDocuments(
        documentsWithContentAndVersions
      );

      dispatch(descriptionResetToBasisOfPresentation());
      if (contentAnalysis) {
        dispatch(updateEntityVersion(contentAnalysis.content.versionNumber));
      }

      dispatch(
        processingSucceeded({
          documentsWithContent: trimmedDocumentsWithContent,
        })
      );
    });
  };
